import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DevicesIcon } from '@phosphor-icons/react/dist/csr/Devices';
import { CardHead } from '../../design-system/composites/Card/Card';
import { ArrowClockwiseIcon } from '@phosphor-icons/react/dist/csr/ArrowClockwise';
import { SignOutIcon } from '@phosphor-icons/react/dist/csr/SignOut';

import { Button } from '../../design-system/primitives/Button/Button';
import {
  ApiError,
  NETWORK_ERROR_CODE,
  PAGE_EXPIRED_CODE,
  TOO_MANY_REQUESTS_CODE,
  api,
  type SessionSummary,
} from '../../lib/api/client';
import styles from './SessionList.module.css';

/**
 * The devices signed in to the caller's own account — `GET /profile/sessions`
 * — with a control to end any one of them, or every one but this device.
 *
 * ## No revoke control on the current row
 *
 * `DELETE /profile/sessions/{handle}` answers `409` for the session making the
 * request (`ProfileSessionController::destroy()`: "the request is allowed,
 * the state is wrong"). A button that is wired to a control the API always
 * refuses is worse than no button — the same rule `UserListScreen.tsx` and
 * `UserActions.tsx` already apply to every row action gated on a permission
 * the caller lacks. `session.is_current` is what this component reads
 * instead of guessing from the handle: the label takes the row a revoke
 * button would have occupied, so the row never looks broken or missing
 * something.
 *
 * ## The load status and the action failure are two different things
 *
 * `status` drives ONLY whether the list itself is on screen — 'loading',
 * 'ready' or 'error', written in exactly one place, `load()`. A row's own
 * revoke, or "sign out everywhere else", never touches it. This is the part-1
 * lesson the plan names directly: an earlier screen once routed a row
 * action's failure through the load status and replaced a fully-loaded table
 * with "could not be loaded" over a single refused click. Here a failed
 * revoke sets `actionFailure`, rendered in its own `role="alert"` region
 * BESIDE the list — the list itself is untouched, because nothing about a
 * refused DELETE means the earlier GET stopped being true.
 *
 * ## Updated in place, not refetched
 *
 * A successful revoke removes exactly the row that succeeded
 * (`session.handle !== handle`); "sign out everywhere else" removes every row
 * but the current one. Neither refetches — `UserActions.tsx`'s `onChanged`
 * carries the identical reasoning: a refetch flashes the loading state over
 * every other row to report a change to one (or, here, all of them at once).
 * This is also what keeps the list honest — a device the person just ended
 * does not linger in it waiting for a page reload to notice.
 *
 * ## The two nullable fields, and the one in seconds
 *
 * `ip_address` and `user_agent` are `string | null` — a session can be
 * recorded before either is known (`SessionSummary`'s own docblock,
 * `client.ts`) — and each renders the same "not recorded" treatment
 * `DataTable`'s empty cell uses: an em dash for sighted users, the word for
 * everyone else. `last_activity` is a UNIX TIMESTAMP IN SECONDS; `new
 * Date(seconds)` alone renders every real session as January 1970, which is
 * why `formatLastActivity` multiplies by 1000 before it ever reaches `Date`.
 */

type LoadStatus = 'loading' | 'ready' | 'error';

/**
 * `ApiExceptionRenderer::STATUSES` — not exported from `client.ts` as a
 * constant, the same trap `UserActions.tsx` and `SettingsScreen.tsx` already
 * document.
 */
const FORBIDDEN_CODE = 'forbidden';

/** UNIX seconds, not milliseconds — see the component docblock. */
function formatLastActivity(seconds: number, locale: string): string {
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(
    new Date(seconds * 1000),
  );
}

export function SessionList() {
  const { t, i18n } = useTranslation();

  const [status, setStatus] = useState<LoadStatus>('loading');
  const [sessions, setSessions] = useState<readonly SessionSummary[]>([]);
  const [loadReference, setLoadReference] = useState<string | null>(null);

  const [actionFailure, setActionFailure] = useState<string | null>(null);
  const [actionReference, setActionReference] = useState<string | null>(null);
  const [othersNotice, setOthersNotice] = useState<string | null>(null);

  const [revokingHandle, setRevokingHandle] = useState<string | null>(null);
  const [revokingOthers, setRevokingOthers] = useState(false);

  /**
   * Read inside every handler below: two clicks in one React batch both see
   * `false`, the same guard `UserActions.tsx` shares across its four actions.
   * One ref for both `revoke()` and `revokeOthers()` — a caller who fires
   * both before the first settles gets the same one-at-a-time rule.
   */
  const inFlight = useRef(false);

  const load = useCallback(async () => {
    setStatus('loading');
    setLoadReference(null);

    try {
      const list = await api.sessions();
      setSessions(list);
      setStatus('ready');
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;
      setLoadReference(error.requestId ?? null);
      setStatus('error');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  function describe(error: ApiError): string {
    switch (error.code) {
      case FORBIDDEN_CODE:
        return t('sessions.notPermitted');
      case TOO_MANY_REQUESTS_CODE:
        return t('auth.throttled');
      case PAGE_EXPIRED_CODE:
        return t('auth.sessionExpired');
      case NETWORK_ERROR_CODE:
        return t('auth.unreachable');
      default:
        // Covers both a genuine server fault and the 404/409 races described
        // in the component docblock — the row this button belonged to either
        // never existed or is gone by the time the request lands, and this
        // generic copy covers both without an oracle this client cannot have.
        return t('sessions.revokeFailed');
    }
  }

  async function revoke(handle: string): Promise<void> {
    if (inFlight.current) return;
    inFlight.current = true;
    setRevokingHandle(handle);
    setActionFailure(null);
    setActionReference(null);
    setOthersNotice(null);

    try {
      await api.revokeSession(handle);
      // Updated in place — see the component docblock's "Updated in place".
      setSessions((current) => current.filter((session) => session.handle !== handle));
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;
      // NEVER setStatus('error') here — see the component docblock's
      // "The load status and the action failure are two different things".
      setActionFailure(describe(error));
      setActionReference(error.requestId ?? null);
    } finally {
      inFlight.current = false;
      setRevokingHandle(null);
    }
  }

  async function revokeOthers(): Promise<void> {
    if (inFlight.current) return;
    inFlight.current = true;
    setRevokingOthers(true);
    setActionFailure(null);
    setActionReference(null);
    setOthersNotice(null);

    try {
      const { revoked } = await api.revokeOtherSessions();
      setSessions((current) => current.filter((session) => session.is_current));
      // `revoked` can genuinely be 0 (context note 4) — nothing changed, so
      // nothing is announced. Claiming a success that did not happen is worse
      // than staying silent.
      if (revoked > 0) setOthersNotice(t('sessions.othersRevoked'));
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;
      setActionFailure(describe(error));
      setActionReference(error.requestId ?? null);
    } finally {
      inFlight.current = false;
      setRevokingOthers(false);
    }
  }

  // Hidden rather than disabled when there is nothing else to end — a control
  // that always succeeds at doing nothing is a control nobody needed to see,
  // the same reasoning `UserActions.tsx`'s row-rule gating already applies.
  const hasOtherSessions = sessions.some((session) => !session.is_current);

  return (
    <section className={styles['panel']} aria-labelledby="sessionsSectionTitle">
      <CardHead id="sessionsSectionTitle" icon={<DevicesIcon size={16} weight="duotone" />} title={t('sessions.title')} />

      {status === 'loading' ? (
        <p className={styles['status']} role="status">
          {t('sessions.loading')}
        </p>
      ) : null}

      {status === 'error' ? (
        <div className={styles['recovery']}>
          <p className={styles['loadFailure']} role="alert">
            {t('sessions.loadFailed')}
          </p>

          <Button
            icon={<ArrowClockwiseIcon size={16} />}
            variant="secondary"
            onClick={() => {
              void load();
            }}
          >
            {t('sessions.retry')}
          </Button>

          {loadReference === null ? null : (
            <p className={styles['reference']}>
              {t('auth.supportReference')}{' '}
              <bdi dir="ltr" data-testid="sessionsLoadRequestId">
                {loadReference}
              </bdi>
            </p>
          )}
        </div>
      ) : null}

      {status === 'ready' ? (
        <>
          <ul className={styles['list']} aria-label={t('sessions.title')}>
            {sessions.map((session) => (
              <li key={session.handle} className={styles['row']}>
                <div className={styles['meta']}>
                  <span className={styles['field']}>
                    <span className={styles['srOnly']}>{t('sessions.ipAddress')}</span>
                    {session.ip_address === null ? (
                      <span className={styles['empty']}>
                        <span aria-hidden="true">—</span>
                        <span className={styles['srOnly']}>{t('table.notRecorded')}</span>
                      </span>
                    ) : (
                      <bdi dir="ltr">{session.ip_address}</bdi>
                    )}
                  </span>

                  <span className={styles['field']}>
                    <span className={styles['srOnly']}>{t('sessions.device')}</span>
                    {session.user_agent === null ? (
                      <span className={styles['empty']}>
                        <span aria-hidden="true">—</span>
                        <span className={styles['srOnly']}>{t('table.notRecorded')}</span>
                      </span>
                    ) : (
                      session.user_agent
                    )}
                  </span>

                  <span className={styles['field']}>
                    <span className={styles['srOnly']}>{t('sessions.lastActive')}</span>
                    <bdi dir="ltr">{formatLastActivity(session.last_activity, i18n.language)}</bdi>
                  </span>
                </div>

                <div className={styles['rowActions']}>
                  {session.is_current ? (
                    <span className={styles['currentLabel']}>{t('sessions.thisDevice')}</span>
                  ) : (
                    <Button
                      icon={<SignOutIcon size={16} />}
                      variant="ghost"
                      loading={revokingHandle === session.handle}
                      onClick={() => {
                        void revoke(session.handle);
                      }}
                    >
                      {t('sessions.revoke')}
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>

          {hasOtherSessions ? (
            <div className={styles['actions']}>
              <Button
                icon={<SignOutIcon size={16} />}
                variant="secondary"
                loading={revokingOthers}
                onClick={() => {
                  void revokeOthers();
                }}
              >
                {t('sessions.revokeOthers')}
              </Button>
            </div>
          ) : null}
        </>
      ) : null}

      {/*
        Mounted from the first frame, empty — see the component docblock's
        "The load status and the action failure are two different things".
      */}
      <div className={styles['alert']} role="alert" aria-atomic="true">
        {actionFailure === null ? null : <span>{actionFailure}</span>}
        {actionReference === null ? null : (
          <span className={styles['reference']}>
            {t('auth.supportReference')}{' '}
            <bdi dir="ltr" data-testid="sessionsActionRequestId">
              {actionReference}
            </bdi>
          </span>
        )}
      </div>

      <div className={styles['notice']} role="status" aria-atomic="true">
        {othersNotice}
      </div>
    </section>
  );
}
